<ng-container [ngSwitch]="type" *ngIf="parent && !hidden">
  <ng-container *ngSwitchCase="'noInput'"></ng-container>
  <!-- Empty container -->
  <div [class]="classes" *ngSwitchCase="'empty'"></div>

  <!-- Label container -->
  <div [class]="classes" *ngSwitchCase="'label'">{{ label }}</div>

  <!-- Radio buttons field type -->
  <div class="lib-form-field-radio" [class]="classes" *ngSwitchCase="'radio'">
    <span *ngIf="label" class="mat-form-field-label-wrapper">
      <label class="mat-form-field-label">{{ label }}</label>
    </span>
    <mat-radio-group [formControl]="control" [id]="name">
      <mat-radio-button
        *ngFor="let option of availableOptions; let i = index; trackBy: trackByValue"
        [value]="option.value"
        id="name-radio{{ i }}"
      >
        {{ option.label }}
      </mat-radio-button>
    </mat-radio-group>
  </div>

  <!-- Checkbox field type -->
  <div class="lib-form-field-checkbox" [class]="classes" [formGroup]="parent" *ngSwitchCase="'checkbox'">
    <mat-checkbox
      [id]="(focusId ?? name) + ididx"
      [disabled]="isReadOnly"
      [formControlName]="name"
      (change)="checkboxChanged($event)"
      [checked]="checked"
    >
      <span class="lib-form-field-checkbox__label">{{
        parent.controls[name]?.value === true && checkedText ? checkedText : label
      }}</span>
    </mat-checkbox>
  </div>

  <!-- toggle field type -->
  <div [ngClass]="stacked ? 'field-toggle-stacked' : 'field-toggle'" [formGroup]="parent" *ngSwitchCase="'toggle'">
    <mat-slide-toggle
      [id]="(focusId ?? name) + ididx"
      class="toggle"
      [disabled]="isReadOnly"
      [formControlName]="name"
      (change)="slideToggleChanged($event)"
    >
    </mat-slide-toggle>
    <span class="field-toggle__label">{{
      parent.controls[name]?.value === true && checkedText ? checkedText : label
    }}</span>
  </div>

  <!-- double toggle field type (has both options shown and label above) -->
  <div class="field-double-toggle" [formGroup]="parent" *ngSwitchCase="'double-toggle'">
    <div class="double-toggle-header">
      <mat-label *ngIf="label" class="double-toggle-label">{{ label }}</mat-label>
    </div>
    <span
      class="double-toggle-unchecked-label"
      [ngClass]="parent.controls[name]?.value === true ? 'slide-toggle-unchecked' : ''"
      >{{ uncheckedText }}</span
    >
    <mat-slide-toggle
      [id]="(focusId ?? name) + ididx"
      class="double-toggle"
      [disabled]="isReadOnly"
      [formControlName]="name"
      (change)="slideToggleChanged($event)"
      [hideIcon]="true"
    >
    </mat-slide-toggle>
    <span
      class="double-toggle-checked-label"
      [ngClass]="parent.controls[name]?.value === false ? 'slide-toggle-unchecked' : ''"
      >{{ checkedText }}</span
    >
  </div>

  <mat-form-field *ngSwitchCase="'multiSelect'" appearance="outline" [class]="classes" [formGroup]="parent" [id]="name">
    <mat-label *ngIf="label">{{ label }}</mat-label>
    <mat-label *ngIf="matLabel" class="mat-label">{{ matLabel }}</mat-label>
    <mat-chip-grid #chipList #chip>
      <mat-chip-row
        *ngFor="let option of selectedOptions; trackBy: trackByValue"
        class="chip"
        [selectable]="selectable"
        [removable]="removable"
        (removed)="remove(option)"
      >
        <div class="chip-text">
          {{ option.shortLabel }}
        </div>
        <div class="cancel">
          <mat-icon class="cancel-icon" matChipRemove *ngIf="removable">cancel</mat-icon>
        </div>
      </mat-chip-row>
    </mat-chip-grid>
    <input
      [placeholder]="placeholder"
      #multiSelectInput
      class="input-field"
      #matACTrigger="matAutocompleteTrigger"
      [formControl]="multiSelectControl"
      [matAutocomplete]="autoMulti"
      [matChipInputFor]="chipList"
      [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
    />
    <button mat-icon-button matSuffix class="expand-button" (click)="openAuto(matACTrigger)">
      <mat-icon class="expand-icon">expand_more</mat-icon>
    </button>
    <mat-autocomplete #autoMulti="matAutocomplete" (optionSelected)="selected($event)" autoActiveFirstOption>
      <div class="select-all-div">
        <mat-checkbox *ngIf="!excludeSelectAllOption" class="select-all" (change)="multipleSelectAll($event)"
          ><span class="select-all-label">-- Select All --</span></mat-checkbox
        >
      </div>
      <mat-option *ngFor="let option of filteredOptions | async; trackBy: trackByValue" [value]="option">
        <mat-checkbox class="check" color="primary" [checked]="selectedOptions.indexOf(option) >= 0"></mat-checkbox>
        {{ option.label }}
      </mat-option>
    </mat-autocomplete>
    <mat-hint *ngIf="hint">{{ hint }}</mat-hint>
    <mat-hint *ngIf="fieldIssues?.updated" class="lib-form-field__update-hint">
      Previous: {{ fieldIssues.updated }}
    </mat-hint>
    <div class="div-required-error" *ngIf="!customRequiredMessage && control?.invalid">
      <mat-error class="required-error" *ngFor="let error of currentErrors" [innerHTML]="error"></mat-error>
    </div>
  </mat-form-field>

  <mat-form-field *ngSwitchCase="'dateRange'" appearance="outline" [class]="classes" [formGroup]="parent" [id]="name">
    <mat-label *ngIf="label">{{ label }}</mat-label>
    <mat-label *ngIf="matLabel" class="mat-label">{{ matLabel }}</mat-label>
    <mat-date-range-input
      [formGroup]="parent"
      class="input"
      [class.disabled]="dateEmpty"
      [rangePicker]="datePickerId"
      [min]="min"
      [max]="max"
      [required]="isRequired"
      (dateChange)="dateRangeChanged($event)"
      [separator]="dateEmpty ? '' : '-'"
    >
      <input matStartDate [formControlName]="name + 'Start'" (dateChange)="dateRangeChanged($event, 'start')" />
      <input
        matEndDate
        [formControlName]="name + 'End'"
        (dateChange)="dateRangeChanged($event, 'end')"
        (blur)="pickerClosed($event)"
      />
    </mat-date-range-input>
    <mat-datepicker-toggle tabindex="-1" class="mat-datepicker-toggle__fix" [for]="datePickerId">
    </mat-datepicker-toggle>
    <mat-date-range-picker #datePickerId (closed)="pickerClosed($event)"></mat-date-range-picker>
    <button mat-button *ngIf="clearButton && !dateEmpty" (click)="clearDateRange()" class="clear-range" tabindex="-1">
      <mat-icon class="icon">highlight_off</mat-icon>
    </button>
    <mat-hint *ngIf="hint">{{ hint }}</mat-hint>
    <mat-hint *ngIf="fieldIssues?.updated" class="lib-form-field__update-hint">
      Previous: {{ fieldIssues.updated }}
    </mat-hint>
    <div class="div-required-error" *ngIf="!customRequiredMessage && parent.status === 'INVALID'">
      <mat-error class="required-error" *ngFor="let error of currentErrors" [innerHTML]="error"></mat-error>
    </div>
  </mat-form-field>

  <!-- Main field type -->
  <mat-form-field *ngSwitchDefault appearance="outline" [class]="classes" [formGroup]="parent" [id]="name">
    <mat-label *ngIf="label">{{ label }}</mat-label>
    <mat-label *ngIf="matLabel" class="mat-label">{{ matLabel }}</mat-label>

    <ng-container [ngSwitch]="type">
      <!-- Date field type -->
      <ng-container *ngSwitchCase="'date'">
        <input
          matInput
          class="input-date"
          [id]="(focusId ?? name) + ididx"
          [formControlName]="name"
          [matDatepicker]="datePickerId"
          [attr.placeholder]="placeholder"
          [min]="min"
          [max]="max"
          [required]="isRequired"
          [readonly]="isReadOnly"
          [disabled]="disabled"
          (dateChange)="datepickerDateChange($event)"
          (focus)="datePickerId.open()"
        />
        <mat-datepicker-toggle tabindex="-1" class="mat-datepicker-toggle__fix" [for]="datePickerId">
        </mat-datepicker-toggle>
        <mat-datepicker #datePickerId></mat-datepicker>
      </ng-container>

      <!-- Select field type -->
      <ng-container *ngSwitchCase="'select'">
        <mat-select
          *ngIf="multiple"
          [id]="focusId"
          [formControlName]="name"
          [multiple]="true"
          [required]="isRequired"
          [disabled]="isReadOnly"
          (selectionChange)="selectSelectionChanged($event)"
          (change)="selectSelectionChanged($event)"
        >
          <mat-checkbox
            *ngIf="!excludeSelectAllOption"
            class="lib-form-field-select-all"
            (change)="multipleSelectAll($event, 'select')"
            ><span class="lib-form-field-checkbox__label">-- Select All --</span></mat-checkbox
          >
          <mat-select-trigger *ngIf="customSelectedDisplay">
            {{ customSelectedDisplay }}
          </mat-select-trigger>
          <mat-option *ngIf="emptyOption" id="emptyOption" [value]="emptyOption.trim()">
            {{ emptyOption.trim() }}
          </mat-option>
          <mat-option *ngFor="let option of availableOptions; trackBy: trackByValue" [value]="option.value">
            {{ option.label }}
          </mat-option>
        </mat-select>
        <mat-select
          *ngIf="!multiple"
          #select
          [id]="(focusId ?? name) + ididx"
          [formControlName]="name"
          [multiple]="false"
          [required]="isRequired"
          [disabled]="isReadOnly"
          (selectionChange)="selectSelectionChanged($event)"
          (blur)="selectUpdate($event, select)"
        >
          <mat-select-trigger *ngIf="customSelectedDisplay">
            {{ customSelectedDisplay }}
          </mat-select-trigger>
          <mat-option *ngIf="emptyOption" [value]="emptyOption.trim()">{{ emptyOption.trim() }}</mat-option>
          <mat-option *ngFor="let option of availableOptions; trackBy: trackByValue" [value]="option.value">
            {{ option.label }}
          </mat-option>
        </mat-select>
        <button mat-button *ngIf="clearButton && showClear" (click)="clear()" class="clear">
          <mat-icon class="icon">highlight_off</mat-icon>
        </button>
      </ng-container>
      <!--Password field type-->
      <ng-container *ngSwitchCase="'password'">
        <input
          class="password"
          matInput
          [id]="(focusId ?? name) + ididx"
          [placeholder]="placeholder"
          [type]="hide ? 'password' : 'text'"
          [formControlName]="name"
        />
        <mat-icon class="position-relative" (click)="passwordToggle()" matSuffix>
          {{ hide ? 'visibility_off' : 'visibility' }}
        </mat-icon>
      </ng-container>
      <!-- Autocomplete field type -->
      <ng-container *ngSwitchCase="'autocomplete'">
        <!-- is being returned as string instead of number but working -->
        <input
          matInput
          #input
          type="text"
          [ngClass]="{ autocomplete: isDelete }"
          [id]="(focusId ?? name) + ididx"
          [formControl]="autocompleteControl"
          [placeholder]="placeholder"
          [matAutocomplete]="auto"
          [readonly]="isReadOnly"
          (blur)="autoCompleteUpdate($event)"
          (input)="autoCompleteFilterChange($event)"
          (keyup)="autoCompleteKeyup($event)"
          (focus)="autoCompleteKeyup($event)"
        />
        <button mat-button *ngIf="length === 'lengthOfArray'" (click)="remove(idx)" class="remove">
          <mat-icon class="icon">highlight_off</mat-icon>
        </button>
        <mat-autocomplete
          #auto="matAutocomplete"
          [displayWith]="suppressCode === true ? displayNoCode : displayProperty"
          (optionSelected)="autocompleteSelected($event)"
          panelWidth="auto"
        >
          <mat-option *ngFor="let option of filteredOptions | async; trackBy: trackByValue" [value]="option">
            <div class="bold-code" *ngIf="option.code && !suppressCode">{{ option.code }} - &nbsp;</div>
            {{ option.label }}
          </mat-option>
        </mat-autocomplete>
        <mat-error class="required-error" *ngIf="autocompleteControl.invalid && customRequiredMessage">
          {{ customRequiredMessage }}
        </mat-error>
        <button
          mat-button
          class="clear-autocomplete"
          *ngIf="clearButton && autocompleteControl.value"
          (click)="clear()"
        >
          <mat-icon class="icon">highlight_off</mat-icon>
        </button>
        <input type="hidden" [formControlName]="name" />
      </ng-container>

      <ng-container *ngSwitchCase="'money'">
        <div class="input-money">
          <span matPrefix class="money" *ngIf="type === 'money'">$&nbsp;</span>
          <input
            matInput
            libMoneyFormatter
            [id]="(focusId ?? name) + ididx"
            [type]="type"
            [formControlName]="name"
            [attr.placeholder]="placeholder"
            [attr.min]="min"
            [attr.max]="max"
            [readonly]="isReadOnly"
            [required]="isRequired"
          />
        </div>
      </ng-container>
      <ng-container *ngSwitchCase="'zipCode'">
        <input
          class="input"
          matInput
          [id]="(focusId ?? name) + ididx"
          type="text"
          [formControlName]="name"
          [attr.placeholder]="placeholder"
          [readonly]="isReadOnly"
          [required]="isRequired"
        />
      </ng-container>
      <ng-container *ngSwitchCase="'decimal'">
        <input
          matInput
          libDecimalFormatter
          [decimalCount]="decimalCount"
          [id]="(focusId ?? name) + ididx"
          [type]="type"
          [formControlName]="name"
          [attr.placeholder]="placeholder"
          [attr.min]="min"
          [attr.max]="max"
          [readonly]="isReadOnly"
          [required]="isRequired"
        />
      </ng-container>

      <!-- textbox field type -->
      <ng-container *ngSwitchCase="'textbox'" class="textarea">
        <textarea
          matInput
          [attr.rows]="min"
          [attr.maxlength]="max"
          [id]="(focusId ?? name) + ididx"
          [formControlName]="name"
        ></textarea>
      </ng-container>

      <!-- Default field type -->
      <ng-container *ngSwitchDefault>
        <input
          class="input"
          matInput
          [id]="(focusId ?? name) + ididx"
          [type]="type"
          [placeholder]="placeholder"
          [formControlName]="name"
          [attr.min]="min"
          [attr.max]="max"
          [readonly]="isReadOnly"
          [required]="isRequired"
        />
        <button mat-button *ngIf="clearButton && showClear" (click)="clear()" class="clear">
          <mat-icon class="icon">highlight_off</mat-icon>
        </button>
      </ng-container>

      <ng-container *ngSwitchCase="'time'">
        <input matInput type="time" [id]="focusId" [formControlName]="name" [attr.placeholder]="placeholder" />
      </ng-container>
    </ng-container>
    <ng-content></ng-content>
    <mat-hint *ngIf="hint">{{ hint }}</mat-hint>
    <mat-hint *ngIf="fieldIssues?.updated" class="lib-form-field__update-hint">
      Previous: {{ fieldIssues.updated }}
    </mat-hint>
    <div class="div-required-error" *ngIf="!customRequiredMessage && parent.status === 'INVALID'">
      <mat-error class="required-error" *ngFor="let error of currentErrors" [innerHTML]="error"></mat-error>
    </div>
  </mat-form-field>
</ng-container>
